import type { AuthenticatedUser, Role } from '../../types/domain'

export const PERMISSION_KEYS = [
  'requisitions.view_own',
  'requisitions.view_all',
  'requisitions.create',
  'requisitions.edit_own',
  'purchases.view',
  'purchases.manage',
  'catalog.view',
  'catalog.manage',
  'item_requests.create',
  'item_requests.manage',
  'users.view',
  'users.manage',
  'permissions.manage',
  'audit.view',
] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

export interface PermissionDefinition {
  key: PermissionKey
  category: string
  label: string
  delegable: boolean
}

export const PERMISSION_CATALOG: readonly PermissionDefinition[] = [
  { key: 'requisitions.view_own', category: 'Requisições', label: 'Visualizar próprias requisições', delegable: true },
  { key: 'requisitions.view_all', category: 'Requisições', label: 'Visualizar todas as requisições', delegable: true },
  { key: 'requisitions.create', category: 'Requisições', label: 'Criar requisição', delegable: true },
  { key: 'requisitions.edit_own', category: 'Requisições', label: 'Editar, bloquear e excluir próprias requisições', delegable: true },
  { key: 'purchases.view', category: 'Compras', label: 'Visualizar compras', delegable: true },
  { key: 'purchases.manage', category: 'Compras', label: 'Gerenciar compras', delegable: true },
  { key: 'catalog.view', category: 'Catálogo', label: 'Visualizar catálogo', delegable: true },
  { key: 'catalog.manage', category: 'Catálogo', label: 'Gerenciar catálogo', delegable: false },
  { key: 'item_requests.create', category: 'Catálogo', label: 'Solicitar cadastro de item', delegable: true },
  { key: 'item_requests.manage', category: 'Catálogo', label: 'Analisar solicitações de item', delegable: false },
  { key: 'users.view', category: 'Administração', label: 'Visualizar usuários', delegable: false },
  { key: 'users.manage', category: 'Administração', label: 'Gerenciar usuários', delegable: false },
  { key: 'permissions.manage', category: 'Administração', label: 'Gerenciar permissões', delegable: false },
  { key: 'audit.view', category: 'Administração', label: 'Visualizar auditoria', delegable: false },
] as const

export const ROLE_DEFAULT_PERMISSIONS: Readonly<Record<Role, readonly PermissionKey[]>> = {
  REQUISITANTE: [
    'requisitions.view_own',
    'requisitions.create',
    'requisitions.edit_own',
    'catalog.view',
    'item_requests.create',
  ],
  COMPRADOR: [
    'purchases.view',
    'purchases.manage',
    'catalog.view',
    'item_requests.create',
  ],
  ADMIN: PERMISSION_KEYS,
}

const permissionKeys = new Set<string>(PERMISSION_KEYS)

export function isPermissionKey(value: unknown): value is PermissionKey {
  return typeof value === 'string' && permissionKeys.has(value)
}

export function defaultPermissionsForRole(role: Role): PermissionKey[] {
  return [...ROLE_DEFAULT_PERMISSIONS[role]]
}

export function hasPermission(
  user: Pick<AuthenticatedUser, 'profile' | 'permissions'> | null | undefined,
  permission: PermissionKey,
): boolean {
  if (!user?.profile.ativo) return false
  if (user.profile.role === 'ADMIN') return true
  return (user.permissions ?? ROLE_DEFAULT_PERMISSIONS[user.profile.role]).includes(permission)
}

export function hasAnyPermission(
  user: Pick<AuthenticatedUser, 'profile' | 'permissions'> | null | undefined,
  permissions: readonly PermissionKey[],
): boolean {
  return permissions.some((permission) => hasPermission(user, permission))
}
